import { GoogleAuthProvider, signInWithPopup, Auth, UserCredential } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, Firestore } from 'firebase/firestore';

/**
 * Utilidad para manejar la autenticación con Google de manera consistente
 * en toda la aplicación, con mejor manejo de errores y compatibilidad.
 */
export const googleAuthHelper = {
  /**
   * Inicia sesión con Google usando popup con manejo mejorado de errores
   * @param auth Instancia de Auth de Firebase
   * @param db Instancia de Firestore
   * @returns Promise con el UserCredential
   */
  async signInWithGoogle(auth: Auth, db: Firestore): Promise<UserCredential> {
    console.log('📱 Iniciando autenticación con Google (helper)...');
    
    // Crear y configurar el proveedor de Google
    const provider = new GoogleAuthProvider();
    
    // Configuraciones adicionales para mejorar la compatibilidad
    provider.setCustomParameters({
      // Forzar selección de cuenta
      prompt: 'select_account',
      // Solicitar permisos específicos
      scope: 'email profile'
    });
    
    // Añadir todos los scopes necesarios
    provider.addScope('email');
    provider.addScope('profile');
    
    try {
      // Intentar iniciar sesión con popup
      const userCredential = await signInWithPopup(auth, provider);
      console.log('✅ Autenticación con Google exitosa:', userCredential.user.displayName);
      
      // Verificar si el usuario ya existe en Firestore
      await this.ensureUserDocumentExists(db, userCredential);
      
      return userCredential;
    } catch (error: any) {
      console.error('❌ Error en autenticación con Google:', error);
      
      // Traducir errores a mensajes más amigables
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Has cerrado la ventana de inicio de sesión antes de completar el proceso.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Tu navegador ha bloqueado la ventana emergente. Por favor, permite ventanas emergentes para este sitio e intenta nuevamente.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('Ya existe una cuenta con este email pero con otro método de inicio de sesión. Intenta con otro método.');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('Este dominio no está autorizado para usar la autenticación de Firebase. Contacta al administrador.');
      } else {
        throw error; // Re-lanzar otros errores
      }
    }
  },
  
  /**
   * Asegura que el documento del usuario exista en Firestore
   * @param db Instancia de Firestore
   * @param userCredential Credenciales del usuario
   */
  async ensureUserDocumentExists(db: Firestore, userCredential: UserCredential): Promise<void> {
    try {
      const { user } = userCredential;
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // Si el documento no existe, crearlo
      if (!userDoc.exists()) {
        console.log('📝 Creando nuevo documento de usuario en Firestore...');
        await setDoc(userDocRef, {
          uid: user.uid,
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL || '',
          followers: 0,
          following: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log('✅ Documento de usuario creado correctamente');
      } else {
        console.log('ℹ️ El usuario ya existe en Firestore');
      }
    } catch (error) {
      console.error('❌ Error al verificar/crear documento de usuario:', error);
      throw error;
    }
  }
};

export default googleAuthHelper;
